import React, { useEffect, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FlexBox, SvgIcon, Icon, Tooltip, MuiTable } from '@kis-technologies/kis-ui';
import { useStyles } from '../styled';
import { MediaTableRowProps } from '../types';

const MediaTableRow = (props: MediaTableRowProps) => {
  const {
    enableDragAndDrop,
    id,
    mediaSource,
    label,
    mediaRecord,
    mediaTypeLabel,
    onDeleteClick,
    onDownloadClick,
    onEditClick,
    onPreviewClick
  } = props;

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  const classes = useStyles({ genreTab: false })();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
    opacity: isDragging ? 0.7 : 1
  };

  useEffect(() => {
    if (mediaSource && mediaTypeLabel === 'Video') {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = mediaSource;
      video.onloadedmetadata = () => {
        setDimensions({
          width: video.videoWidth,
          height: video.videoHeight
        });
      };
    } else if (mediaSource) {
      const img = new Image();
      img.src = mediaSource;
      img.onload = () => {
        setDimensions({
          width: img.width,
          height: img.height
        });
      };
    }
  }, [mediaSource, mediaTypeLabel]);

  // detect drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  // detect if user actually dragged more than a few px
  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragStart) {
      const distance = Math.hypot(e.clientX - dragStart.x, e.clientY - dragStart.y);
      if (distance > 5) {
        setIsDragging(true);
      }
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging) {
      const target = (e.target as HTMLElement).closest('[data-action]');
      if (target) {
        const action = target.getAttribute('data-action');
        if (action === 'preview' && onPreviewClick) onPreviewClick();
        if (action === 'download' && onDownloadClick) onDownloadClick();
        if (action === 'edit' && onEditClick) onEditClick();
        if (action === 'delete' && onDeleteClick) onDeleteClick();
      }
    }
    setIsDragging(false);
    setDragStart(null);
  };

  const getFileName = () => {
    if (mediaRecord?.imageName) return mediaRecord.imageName;
    if (mediaRecord?.name) return mediaRecord.name;
    return '-';
  };

  return (
    <>
      <MuiTable.TableRow
        className={classes.tableRow}
        onMouseUp={handleMouseUp}
        {...(enableDragAndDrop && {
          ref: setNodeRef,
          ...attributes,
          ...listeners,
          onMouseDown: handleMouseDown,
          onMouseMove: handleMouseMove,
          style
        })}
      >
        {/* SLIDE/PROMO NAME */}
        {label && (
          <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>{label}</MuiTable.TableCell>
        )}
        {/* FILE NAME */}
        <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableFileNameCell}`}>
          {getFileName()}
        </MuiTable.TableCell>
        {/* DIMENSIONS */}
        <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableDimensionsCell}`}>
          {dimensions ? `${dimensions.width}px x ${dimensions.height}px` : '-'}
        </MuiTable.TableCell>
        {/* ACTIONS */}
        <MuiTable.TableCell className={`${classes.tableCell} ${classes.tableActionsCell}`}>
          <FlexBox justifyContent="end" gap="10px">
            {/* PREVIEW */}
            {onPreviewClick && (
              <Tooltip title={`Preview ${mediaTypeLabel}`}>
                <FlexBox className={classes.actionIconBox} data-action="preview">
                  <Icon name="VisibilityOutlined" />
                </FlexBox>
              </Tooltip>
            )}
            {/* DOWNLOAD */}
            {onDownloadClick && (
              <Tooltip title={`Download ${mediaTypeLabel}`}>
                <FlexBox className={classes.actionIconBox} data-action="download">
                  <Icon name="FileDownloadOutlined" />
                </FlexBox>
              </Tooltip>
            )}
            {/* EDIT */}
            {onEditClick && (
              <Tooltip title={`Edit ${mediaTypeLabel}`}>
                <FlexBox className={classes.actionSvgIconBox} data-action="edit">
                  <SvgIcon name="edit_circle" size={36} />
                </FlexBox>
              </Tooltip>
            )}
            {/* DELETE */}
            {onDeleteClick && mediaRecord && (
              <Tooltip title={`Delete ${mediaTypeLabel}`}>
                <FlexBox className={classes.actionSvgIconBox} data-action="delete">
                  <SvgIcon name="exit_circle" size={36} />
                </FlexBox>
              </Tooltip>
            )}
          </FlexBox>
        </MuiTable.TableCell>
      </MuiTable.TableRow>
    </>
  );
};

export default MediaTableRow;
